// iPad: the on-screen keyboard must only open when the user TAPS a text field.
//
// Drawers, dialogs and popovers (Radix FocusScope) focus their first field on
// open, and some components pass `autoFocus` / call `.focus()` themselves. On
// desktop that's harmless; on iPadOS every one of those raises the keyboard,
// which covers half the screen — opening the lead Filter drawer popped the
// keyboard over the sections the user came to tap (user, 2026-09-15).
//
// So on iPads only, a keyboard field may take focus when:
//   1. the user's own touch/pen press landed on that field (or its label), or
//   2. focus is moving from one text field to another (keyboard is already up,
//      e.g. Tab / "next" between form fields), or
//   3. a hardware key was pressed just before (Magic Keyboard shortcuts / Tab).
// Anything else — auto-focus on open, programmatic focus after a button tap —
// is skipped. The field still works: one tap on it focuses normally.
//
// Desktop, Android and phones are untouched (install is a no-op off iPad).
// Pairs with touchKeyboardDismiss.ts, which closes the keyboard on outside taps.

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** How long a tap / key press "authorises" a focus that follows it. */
const USER_INTENT_WINDOW_MS = 1000;

function isKeyboardField(el: unknown): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(el.type) && !el.readOnly;
  }
  return el.isContentEditable;
}

/** iPadOS 13+ reports itself as a Mac; the touch points give it away. */
export function isIPad(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/iPad/.test(navigator.userAgent)) return true;
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

let installed = false;

export function installIPadAutoFocusGuard() {
  if (installed || typeof window === "undefined" || !isIPad()) return;
  installed = true;

  let lastTouchTarget: Element | null = null;
  let lastTouchAt = 0;
  let lastKeyAt = 0;

  const tappedOn = (field: HTMLElement): boolean => {
    if (!lastTouchTarget || Date.now() - lastTouchAt > USER_INTENT_WINDOW_MS) {
      return false;
    }
    if (field.contains(lastTouchTarget)) return true;
    const label = lastTouchTarget.closest("label");
    return !!label && (label as HTMLLabelElement).control === field;
  };

  const isAllowed = (field: HTMLElement): boolean => {
    if (tappedOn(field)) return true;
    const active = document.activeElement;
    if (active !== field && isKeyboardField(active)) return true;
    return Date.now() - lastKeyAt <= USER_INTENT_WINDOW_MS;
  };

  window.addEventListener(
    "pointerdown",
    (e) => {
      if (e.pointerType === "mouse") return;
      lastTouchTarget = e.target as Element | null;
      lastTouchAt = Date.now();
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    "keydown",
    () => {
      lastKeyAt = Date.now();
    },
    { capture: true, passive: true }
  );

  // Programmatic focus (Radix auto-focus, React `autoFocus`, `.focus()`):
  // stop it before it happens so the keyboard never flashes up.
  const originalFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function focus(
    this: HTMLElement,
    options?: FocusOptions
  ) {
    if (isKeyboardField(this) && !isAllowed(this)) return;
    return originalFocus.call(this, options);
  };

  // Fallback for focus that bypasses `.focus()` (the HTML `autofocus`
  // attribute): undo it straight away.
  document.addEventListener(
    "focusin",
    (e) => {
      const field = e.target;
      if (!isKeyboardField(field) || tappedOn(field)) return;
      const cameFromField = isKeyboardField(e.relatedTarget);
      if (cameFromField || Date.now() - lastKeyAt <= USER_INTENT_WINDOW_MS) {
        return;
      }
      field.blur();
    },
    true
  );
}
